import React, {
  ChangeEvent,
  FC,
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Routes from "models/routes";
import { GridToolbarContainer } from "@mui/x-data-grid";
import CommonButton from "components/common/ui/CommonButton";
import AddIcon from "@mui/icons-material/Add";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { Box, Grid, InputAdornment, Tooltip } from "@mui/material";
import { css } from "styled-components";
import ColorCircularProgress from "components/common/ui/ColorCircularProgress";
import RecipeStepGridItemBox from "components/food/ui/recipe/RecipeStepGridItemBox";
import SpacedTextInput from "components/common/ui/SpacedTextInput";
import CircleIconButton from "components/common/ui/CircleIconButton";
import ClearIcon from "@mui/icons-material/Clear";
import SoftSquareIconButton from "components/common/ui/SoftSquareIconButton";
import SearchIcon from "@mui/icons-material/Search";
import { useAppDispatch, useAppSelector } from "hooks/useReduxStore";
import { actions as FoodItemOptionsActions } from "store/slices/food/foodItemOptionsSlice";
import foodItemOptionsSelector from "store/selectors/food/foodItemOptionsSelector";
import { usePromptSnackBar } from "components/common/ui/PromptSnackBarProvider";
import dayjs from "dayjs";

interface IngredientListTableToolbarProps {
  setKeywords: (keywords: string | undefined) => void;
}

const IngredientListTableToolbar: FC<IngredientListTableToolbarProps> = ({
  setKeywords,
}) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const promptSnackBar = usePromptSnackBar();
  const isDownloading = useAppSelector(
    foodItemOptionsSelector.foodIsDownloading,
  );

  // Parse initial keywords from URL params
  const initialKeywords = useMemo(
    () => searchParams.get("keywords") || undefined,
    [searchParams],
  );

  const [displayKeywords, setDisplayKeywords] = useState<string | undefined>(
    initialKeywords,
  );

  // Sync local state with URL params when they change externally
  useEffect(() => {
    setDisplayKeywords(initialKeywords);
  }, [initialKeywords]);

  /**
   * Handle the keywords change
   * @param event
   */
  const handleKeywordsChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const newValue = event.target.value ?? undefined;
    setDisplayKeywords(newValue);
  };

  /**
   * Handle click on create button
   */
  const handleCreateClick = (): void => {
    navigate(Routes.FOOD_INGREDIENT_CREATE, {
      replace: false,
    });
  };

  /**
   * Handle download all ingredients button click
   */
  const handleDownloadClick = useCallback(() => {
    dispatch(
      FoodItemOptionsActions.downloadAllFoodItems({
        downloadType: "ingredient",
      }),
    )
      .unwrap()
      .then((response) => {
        // Trigger ZIP file download
        const url = window.URL.createObjectURL(
          new Blob([response], { type: "application/zip" }),
        );
        const alink = document.createElement("a");
        alink.href = url;
        alink.download = `Jasper ingredients downloaded at ${dayjs().toISOString()}.zip`;
        document.body.appendChild(alink);
        alink.click();
        alink.remove();
      })
      .catch((err) => {
        promptSnackBar.setPromptProps({
          isOpen: true,
          message: `Download error. ${err}`,
          severity: "error",
        });
      });
  }, [dispatch, promptSnackBar]);

  /**
   * Handle click on search button
   */
  const handleSearchClick = useCallback(
    (searchingKeywords: string | undefined) => (): void => {
      setKeywords(searchingKeywords);
    },
    [setKeywords],
  );

  /**
   * Handle click on clear icon in searching bar
   */
  const handleClearClick = useCallback(() => {
    handleSearchClick(undefined)();
    setDisplayKeywords(undefined);
  }, [handleSearchClick]);

  /**
   * Handle keypress while focus on keyword field
   */
  const handleKeypress = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "Enter") {
        handleSearchClick(displayKeywords)();
      } else if (event.key === "Escape") {
        handleClearClick();
      }
    },
    [displayKeywords, handleClearClick, handleSearchClick],
  );

  return (
    <GridToolbarContainer>
      <Grid container spacing={1} columns={24}>
        <Grid item xs={24} sm={24} md={7} lg={8}>
          <Box sx={{ display: "flex" }}>
            <CommonButton
              text="Create"
              positive
              startIcon={<AddIcon />}
              onClick={handleCreateClick}
            />
            <CommonButton
              text="All ingredients"
              positive={!isDownloading}
              startIcon={
                isDownloading ? (
                  <ColorCircularProgress
                    customColor={css`rgba(0, 0, 0, 0.26)`}
                    size={20}
                  />
                ) : (
                  <FileDownloadIcon />
                )
              }
              onClick={handleDownloadClick}
              disabled={isDownloading}
            />
          </Box>
        </Grid>
        <Grid item xs={24} sm={24} md={17} lg={16}>
          <RecipeStepGridItemBox>
            <SpacedTextInput
              size="small"
              label="Keywords"
              InputProps={{
                endAdornment: displayKeywords ? (
                  <InputAdornment position="end">
                    <CircleIconButton
                      onClick={handleClearClick}
                      sizepx={24}
                      natural
                    >
                      <ClearIcon />
                    </CircleIconButton>
                  </InputAdornment>
                ) : null,
              }}
              onChange={handleKeywordsChange}
              onKeyDown={handleKeypress}
              value={displayKeywords ?? ""}
            />
            <Box>
              <Tooltip title="Search">
                <Box padding="3px 1rem 3px 0.5rem">
                  <SoftSquareIconButton
                    onClick={handleSearchClick(displayKeywords)}
                    sizepx={34}
                    positive
                  >
                    <SearchIcon />
                  </SoftSquareIconButton>
                </Box>
              </Tooltip>
            </Box>
          </RecipeStepGridItemBox>
        </Grid>
      </Grid>
    </GridToolbarContainer>
  );
};

export default IngredientListTableToolbar;
